import {
    Sanction,
    SanctionType,
    DisciplinarySubjectType,
    DisciplinarySuspensionType,
    DisciplinaryCase,
    DisciplinarySession,
    DisciplinaryCaseStatus,
    DisciplinarySessionStatus,
} from '../../types';
import {
    DisciplinaryReason,
    LeagueDisciplinaryRule,
    LeagueDisciplinaryRuleDetail,
} from '../../types/disciplinary-refactor.types';

export function disciplinarySessionDbToCamel(row: any | null | undefined): DisciplinarySession | null {
    if (!row) return null;
    const rawMembers = row.committee_members;
    const committeeMembers = Array.isArray(rawMembers)
        ? rawMembers.map((m: any) => ({
            role: m?.role ?? '',
            nameAr: m?.name_ar ?? m?.nameAr ?? '',
            displayOrder: Number(m?.display_order ?? m?.displayOrder ?? 0),
        }))
        : [];
    return {
        id: row.id,
        leagueId: row.league_id ?? null,
        season: row.season,
        meetingDate: row.meeting_date,
        meetingTime: row.meeting_time || null,
        meetingNumber: row.meeting_number ?? null,
        committeeName: row.committee_name,
        location: row.location || null,
        status: (row.status || DisciplinarySessionStatus.SCHEDULED) as DisciplinarySessionStatus,
        quorum: row.quorum || null,
        chairperson: row.chairperson || null,
        secretary: row.secretary || null,
        attendees: row.attendees || null,
        agenda: row.agenda || null,
        notes: row.notes || null,
        publicationNumber: row.publication_number || null,
        subjectAr: row.subject_ar || null,
        publishedAt: row.published_at || null,
        publishedBy: row.published_by || null,
        summonsRecipientAr: row.summons_recipient_ar || null,
        summonsDeadline: row.summons_deadline || null,
        sectionOrder: Array.isArray(row.section_order) ? (row.section_order as string[]) : null,
        committeeMembers,
        siteReportId: row.site_report_id ?? null,
        siteFileUrl: row.site_file_url || null,
        siteArticleId: row.site_article_id ?? null,
        siteNewsArticleId: row.site_news_article_id || null,
        sitePublishedAt: row.site_published_at || null,
        createdAt: row.created_at,
        createdBy: row.created_by || null,
        updatedAt: row.updated_at || null,
        updatedBy: row.updated_by || null,
        activeCaseCount: row.active_case_count ?? null,
        totalCaseCount: row.total_case_count ?? null,
        firstDecisionDate: row.first_decision_date || null,
        lastDecisionDate: row.last_decision_date || null,
    };
}

export function disciplinaryCaseDbToCamel(row: any): DisciplinaryCase {
    const session = disciplinarySessionDbToCamel(row.session);
    return {
        id: row.id,
        sessionId: row.session_id || null,
        matchId: row.match_id || null,
        matchDate: row.match_date || null,
        matchSeason: row.match_season || null,
        homeTeamId: row.home_team_id || null,
        awayTeamId: row.away_team_id || null,
        leagueGroupId: row.league_group_id || null,
        caseNumber: row.case_number || null,
        status: (row.status || DisciplinaryCaseStatus.DRAFT) as DisciplinaryCaseStatus,
        title: row.title,
        caseType: (row.case_type || 'match_sanction') as DisciplinaryCase['caseType'],
        displayOrder: row.display_order ?? null,
        caseData: row.case_data || null,
        // Read by the match section editor: unmapped, its save wiped both.
        bodyNarrativeAr: row.body_narrative_ar ?? null,
        caseOutcome: row.case_outcome ?? null,
        hearingOrder: row.hearing_order ?? null,
        subjectType: row.subject_type || null,
        playerId: row.player_id || null,
        staffMemberId: row.staff_member_id || null,
        teamId: row.team_id || null,
        playerName: row.player_name || null,
        staffMemberName: row.staff_member_name || null,
        teamName: row.team_name || null,
        subjectSnapshot: row.subject_snapshot || null,
        subjectSummary: row.subject_summary || null,
        allegations: row.allegations || null,
        factsEstablished: row.facts_established || null,
        analysis: row.analysis || null,
        decision: row.decision || null,
        narrativeSummary: row.narrative_summary || null,
        decisionDate: row.decision_date || null,
        sanctionsSummary: row.sanctions_summary || null,
        recommendations: row.recommendations || null,
        notes: row.notes || null,
        attachments: row.attachments || null,
        createdAt: row.created_at,
        createdBy: row.created_by || null,
        updatedAt: row.updated_at || null,
        updatedBy: row.updated_by || null,
        publishedAt: row.published_at || null,
        session,
    };
}

// DB -> Front mapping
export function sanctionDbToCamel(row: any): Sanction {
    const subjectType: DisciplinarySubjectType = row.subject_type || DisciplinarySubjectType.PLAYER;
    const suspensionType: DisciplinarySuspensionType =
        row.suspension_type || DisciplinarySuspensionType.NONE;
    const linkedCase = row.case ? disciplinaryCaseDbToCamel(row.case) : null;

    // Map disciplinary reason if present (from JOIN)
    let disciplinaryReason: DisciplinaryReason | undefined;
    if (row.disciplinary_reasons) {
        disciplinaryReason = disciplinaryReasonDbToCamel(row.disciplinary_reasons);
    }

    return {
        id: row.id,
        leagueId: row.league_id,
        reasonId: row.reason_id || null,
        disciplinaryReason,
        subjectType,
        playerId: row.player_id || null,
        staffMemberId: row.staff_member_id || null,
        teamId: row.team_id || null,
        matchId: row.match_id,
        entryContextMatchId: row.entry_context_match_id || null,
        caseId: row.case_id || linkedCase?.id || null,
        type: row.type,
        reason: row.reason || undefined,
        decisionDate: row.decision_date,
        suspensionType,
        suspensionMatchCount: row.suspension_match_count ?? null,
        suspensionSuspendedValue: row.suspension_suspended_value ?? null,
        suspensionStartDate: row.suspension_start_date || null,
        suspensionEndDate: row.suspension_end_date || null,
        suspensionMatches: row.suspension_matches ?? 0,
        fineAmount: row.fine_amount ?? undefined,
        matchesServed: row.matches_served ?? 0,
        isCancelled: row.is_cancelled ?? false,
        cancelledByCaseId: row.cancelled_by_case_id ?? null,
        cancelledByCase: row.cancelled_by_case ?? null,
        notes: row.notes || undefined,
        subjectNotes: row.subject_notes || undefined,
        createdVia: row.created_via || undefined,
        createdAt: row.created_at,
        createdBy: row.created_by,
        createdByName: row.created_by_name,
        updatedAt: row.updated_at || null,
        updatedBy: row.updated_by || null,
        updatedByName: row.updated_by_name || null,
        subjectDisplayName: row.subject_display_name || undefined,
        teamName: row.team_name || undefined,
        staffRole: row.staff_role || undefined,
        caseNumber: row.case_number || linkedCase?.caseNumber || null,
        caseTitle: row.case_title || linkedCase?.title || null,
        caseStatus: (row.case_status || linkedCase?.status || undefined) as DisciplinaryCaseStatus | undefined,
        sessionId: row.session_id || linkedCase?.sessionId || null,
        linkedCase,
        fafEventId: row.faf_event_id || null,
        infractionId: row.infraction_id ?? null,
        modifier: row.modifier ?? {},
        appliedConsequence: row.applied_consequence ?? null,
    } as Sanction;
}

// Front -> DB mapping
export function sanctionCamelToDb(input: Partial<Sanction>): any {
    if (!input) return input;

    const subjectType = input.subjectType || DisciplinarySubjectType.PLAYER;
    const suspensionType = input.suspensionType || DisciplinarySuspensionType.NONE;

    let suspension = 0;
    if (suspensionType === DisciplinarySuspensionType.MATCH_COUNT) {
        if (typeof input.suspensionMatchCount === 'number') {
            suspension = input.suspensionMatchCount;
        } else if (typeof input.suspensionMatches === 'number') {
            suspension = input.suspensionMatches;
        }
    }

    // Backend safety default: red cards must have at least 1 (mirrors DB CHECK)
    if (
        (input.type === SanctionType.RED_CARD_DIRECT || input.type === SanctionType.RED_CARD_TWO_YELLOWS) &&
        suspension < 1
    ) {
        suspension = 1; // minimal default; front-end already tries to set config-based value
    }

    // Yellow cards must never carry suspension
    if (input.type === SanctionType.YELLOW_CARD) {
        suspension = 0;
    }

    return {
        id: input.id,
        league_id: input.leagueId || null, // ✅ Added league_id support
        reason_id: input.reasonId || null, // ✅ Added reason_id support for new disciplinary structure
        subject_type: subjectType,
        player_id: subjectType === DisciplinarySubjectType.PLAYER ? input.playerId || null : null,
        staff_member_id:
            subjectType === DisciplinarySubjectType.COACH || subjectType === DisciplinarySubjectType.STAFF
                ? input.staffMemberId || null
                : null,
        team_id:
            subjectType === DisciplinarySubjectType.TEAM
                ? input.teamId || null
                : subjectType === DisciplinarySubjectType.COACH || subjectType === DisciplinarySubjectType.STAFF
                    ? input.teamId || null
                    : input.teamId || null,
        match_id: input.matchId || null,
        entry_context_match_id: input.entryContextMatchId || null,
        type: input.type,
        decision_date: input.decisionDate,
        suspension_type: suspensionType,
        suspension_match_count:
            suspensionType === DisciplinarySuspensionType.MATCH_COUNT ? suspension : null,
        // start_date can apply to either kind of suspension — manual PVs commonly
        // write "تسري من تاريخ YYYY/MM/DD" on match-count suspensions too. Persist
        // whatever the form provided whenever the suspension is non-NONE.
        suspension_start_date:
            suspensionType !== DisciplinarySuspensionType.NONE ? input.suspensionStartDate || null : null,
        suspension_end_date:
            suspensionType === DisciplinarySuspensionType.DATE_RANGE ? input.suspensionEndDate || null : null,
        suspension_suspended_value:
            suspensionType !== DisciplinarySuspensionType.NONE && typeof input.suspensionSuspendedValue === 'number'
                ? input.suspensionSuspendedValue
                : null,
        suspension_matches: suspensionType === DisciplinarySuspensionType.MATCH_COUNT ? suspension : 0,
        fine_amount: typeof input.fineAmount === 'number' ? input.fineAmount : null,
        matches_served: typeof input.matchesServed === 'number' ? input.matchesServed : 0,
        is_cancelled: !!input.isCancelled,
        notes: input.notes || null,
        subject_notes: input.subjectNotes || null,
        // Only when the payload carries them: an edit built field by field (the
        // sanction modal) omits the PV case, the FAF link, the reason and meta,
        // and writing them as null detached edited sanctions from their PV.
        ...(input.caseId !== undefined || input.linkedCase !== undefined
            ? { case_id: input.caseId || input.linkedCase?.id || null }
            : {}),
        ...(input.reason !== undefined ? { reason: input.reason || null } : {}),
        ...(input.createdVia !== undefined ? { created_via: input.createdVia || null } : {}),
        ...(input.fafEventId !== undefined ? { faf_event_id: input.fafEventId } : {}),
        ...(input.meta !== undefined ? { meta: input.meta ?? {} } : {}),
        ...(input.infractionId !== undefined ? { infraction_id: input.infractionId } : {}),
        ...(input.modifier !== undefined ? { modifier: input.modifier } : {}),
        ...(input.appliedConsequence !== undefined ? { applied_consequence: input.appliedConsequence } : {}),
    };
}

export function disciplinarySessionCamelToDb(input: Partial<DisciplinarySession>): any {
    if (!input) return input;

    const members = Array.isArray(input.committeeMembers)
        ? input.committeeMembers.map((m, idx) => ({
            role: m.role ?? '',
            name_ar: m.nameAr ?? '',
            display_order: Number.isFinite(m.displayOrder) ? m.displayOrder : idx,
        }))
        : undefined;

    return {
        id: input.id,
        season: input.season,
        meeting_date: input.meetingDate,
        meeting_time: input.meetingTime || null,
        meeting_number: input.meetingNumber ?? null,
        committee_name: input.committeeName || 'Commission de discipline',
        location: input.location || null,
        status: input.status || DisciplinarySessionStatus.SCHEDULED,
        quorum: input.quorum || null,
        chairperson: input.chairperson || null,
        secretary: input.secretary || null,
        attendees: input.attendees || null,
        agenda: input.agenda || null,
        notes: input.notes || null,
        publication_number: input.publicationNumber || null,
        published_at: input.publishedAt || null,
        published_by: input.publishedBy || null,
        summons_recipient_ar: input.summonsRecipientAr || null,
        summons_deadline: input.summonsDeadline || null,
        ...(members !== undefined ? { committee_members: members } : {}),
    };
}

export function disciplinaryCaseCamelToDb(input: Partial<DisciplinaryCase>): any {
    if (!input) return input;

    return {
        id: input.id,
        session_id: input.sessionId || input.session?.id || null,
        match_id: input.matchId || null,
        case_number: input.caseNumber || null,
        status: input.status || DisciplinaryCaseStatus.DRAFT,
        title: input.title,
        case_type: input.caseType || 'match_sanction',
        league_group_id: input.leagueGroupId || null,
        display_order: input.displayOrder ?? null,
        case_data: input.caseData ?? {},
        hearing_order: input.hearingOrder ?? null,
        subject_type: input.subjectType || null,
        player_id: input.playerId || null,
        staff_member_id: input.staffMemberId || null,
        team_id: input.teamId || null,
        subject_snapshot: input.subjectSnapshot || null,
        subject_summary: input.subjectSummary || null,
        allegations: input.allegations || null,
        facts_established: input.factsEstablished || null,
        analysis: input.analysis || null,
        decision: input.decision || null,
        narrative_summary: input.narrativeSummary || null,
        decision_date: input.decisionDate || null,
        sanctions_summary: input.sanctionsSummary || null,
        recommendations: input.recommendations || null,
        notes: input.notes || null,
        attachments: input.attachments || null,
        published_at: input.publishedAt || null,
    };
}

// =====================================================
// NEW: Disciplinary Reasons & League Rules Mappers
// =====================================================

/**
 * Map disciplinary reason from DB to camelCase
 */
export function disciplinaryReasonDbToCamel(row: any): DisciplinaryReason {
    return {
        id: row.id,
        leagueId: row.league_id,
        copiedFromId: row.copied_from_id ?? null,
        code: row.code,
        reasonAr: row.reason_ar,
        reasonFr: row.reason_fr || null,
        description: row.description || null,
        isActive: row.is_active ?? true,
        displayOrder: row.display_order ?? 0,
        sanctionTarget: row.sanction_target || null,
        defaultFineAmount: row.default_fine_amount || null,
        defaultSuspensionMatches: row.default_suspension_matches || null,
        suspensionDurationDays: row.suspension_duration_days || null,
        articleReference: row.article_reference || null,
        articleCodes: Array.isArray(row.article_codes) ? row.article_codes : [],
        pvLabelAr: row.pv_label_ar ?? null,
        conditionsAr: row.conditions_ar || null,
        metadata: row.metadata || {},
        fafCode: row.faf_code ?? null,
        canonicalInfractionId: row.canonical_infraction_id ?? null,
        canonicalModifier: row.canonical_modifier ?? {},
        reportEventTypes: Array.isArray(row.report_event_types) ? row.report_event_types : [],
        createdAt: row.created_at,
        createdBy: row.created_by || null,
        updatedAt: row.updated_at,
        updatedBy: row.updated_by || null,
    };
}

/**
 * Map league disciplinary rule from DB to camelCase
 */
export function leagueRuleDbToCamel(row: any): LeagueDisciplinaryRule | LeagueDisciplinaryRuleDetail {
    const base: LeagueDisciplinaryRule = {
        id: row.id,
        leagueId: row.league_id,
        reasonId: row.reason_id,
        fineAmount: Number(row.fine_amount) || 0,
        suspensionType: row.suspension_type as DisciplinarySuspensionType,
        defaultSuspensionMatches: row.default_suspension_matches ?? null,
        defaultSuspensionDays: row.default_suspension_days ?? null,
        isActive: row.is_active ?? true,
        validFrom: row.valid_from,
        validTo: row.valid_to || null,
        metadata: row.metadata || {},
        createdAt: row.created_at,
        createdBy: row.created_by || null,
        updatedAt: row.updated_at,
        updatedBy: row.updated_by || null,
    };

    // If the query included joins, add the related data
    if (row.disciplinary_reasons || row.leagues) {
        const detailed: LeagueDisciplinaryRuleDetail = {
            ...base,
            reason: row.disciplinary_reasons ? disciplinaryReasonDbToCamel(row.disciplinary_reasons) : null as any,
            league: row.leagues
                ? {
                    id: row.leagues.id,
                    name: row.leagues.name,
                    nameAr: row.leagues.name_ar || null,
                    code: row.leagues.code,
                }
                : null as any,
        };
        return detailed;
    }

    return base;
}
